import { FC, Fragment, Suspense, useCallback, useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { Container, CssBaseline, ThemeProvider, Theme, StyledEngineProvider, Box, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import { NavBar } from 'TaxAnalyticsUI/navbar';
import { Footer } from 'TaxAnalyticsUI/footer';
import { Home, Methodo, Workspace, Order, Login, Reply, Logout } from './components/pages';
import { DarkTheme, LightTheme } from './themes';
import { Offer } from './components/pages/services';
import { SuspenseFallback } from 'TaxAnalyticsUI/SuspenseFallback';
import { snackbarState } from 'TaxAnalyticsState/portalState';
import { useRecoilState } from 'recoil';
import { ProtectedRoute } from './components/hoc/auth';
import { CustomNavigationClient, msalInstance } from 'TaxAnalyticsHelpers/AADHelper';
import Particles from "react-particles";
import { loadFull } from "tsparticles";
import { Engine } from 'tsparticles-engine';
import { connect, links, stars } from 'TaxAnalyticsUI/particles';
import Routes from 'TaxAnalyticsHelpers/Routes';
import moment from 'moment-timezone';



declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme { }
}

var tz = moment.tz.guess();
moment.tz.setDefault(tz);


const App: FC = (props: any) => {
    const location = useLocation();
    const [snackbar, setSnackbarState] = useRecoilState(snackbarState)
    const history = useHistory();
    const navigationClient = new CustomNavigationClient(history);
    msalInstance.setNavigationClient(navigationClient);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const handleClose = (event: any, reason: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarState({
            ...snackbar, open: false
        });
    };

    const particlesInit = useCallback(async (engine: Engine) => {
        await loadFull(engine);
    }, []);

    const particlesLoaded = useCallback(async (container: any | undefined) => {
        await container;
    }, []);
    return (
        <Box display="flex" height='100vh' >
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={DarkTheme} >
                    <CssBaseline />
                    <Box display="flex" flexDirection="column" flexGrow={1}>
                        <Box>
                            <NavBar />
                        </Box>
                        <Box flexGrow={1} display="flex" id='routeBase'>
                            <Suspense fallback={<SuspenseFallback options={stars} init={particlesInit} loaded={particlesLoaded} />}>
                                <Switch>
                                    <Route exact path={Routes.home.path}>
                                        {(props) => {
                                            return (<Fragment>
                                                <Particles id="tsparticles" options={stars} init={particlesInit} loaded={particlesLoaded} />
                                                <Container maxWidth="lg" component="main"><Box pb={6}><Home /></Box> </Container>
                                            </Fragment>)
                                        }
                                        }
                                    </Route>
                                    <Route exact path={Routes.reply.path}>
                                        {(props) => {
                                            return (<Fragment>
                                                <Particles id="tsparticles" options={stars} init={particlesInit} loaded={particlesLoaded} />
                                                <Container maxWidth="lg" component="main"><Box pb={6}><Reply /></Box> </Container>
                                            </Fragment>)
                                        }
                                        }
                                    </Route>
                                    <Route exact path={Routes.logout.path}>
                                        {(props) => {
                                            return (<Fragment>
                                                <Particles id="tsparticles" options={stars} init={particlesInit} loaded={particlesLoaded} />
                                                <Container maxWidth="lg" component="main"><Box pb={6}><Logout /></Box> </Container>
                                            </Fragment>
                                            )
                                        }
                                        }
                                    </Route>
                                    <Route exact path={Routes.login.path}>
                                        {(props) => {
                                            return (<Fragment>
                                                <Particles id="tsparticles" options={stars} init={particlesInit} loaded={particlesLoaded} />
                                                <Container maxWidth="lg" component="main"><Box pb={6}><Login redirectUrl={(props.location as any).state?.redirectUrl} /></Box> </Container>
                                            </Fragment>)
                                        }}
                                    </Route>
                                    <Route path={Routes.offer.path}>
                                        {(props) => {
                                            return (<Fragment>
                                                <Particles id="tsparticles" options={links} init={particlesInit} loaded={particlesLoaded} />
                                                <Container maxWidth="lg" component="main"><Box pb={6}><Offer /></Box> </Container>
                                            </Fragment>)
                                        }}
                                    </Route>
                                    <Route exact path={Routes.methodo.path}>
                                        {(props) => {
                                            return (
                                                <Fragment>
                                                    <Particles id="tsparticles" options={connect} init={particlesInit} loaded={particlesLoaded} />
                                                    <Container maxWidth="lg" component="main"><Box pb={6}><Methodo /></Box> </Container>
                                                </Fragment>)
                                        }}
                                    </Route>
                                    <ProtectedRoute path={Routes.workspace.path}>
                                        <Workspace />
                                    </ProtectedRoute>
                                    <Route path={Routes.order.path}>
                                        {(props) => {
                                            return (
                                                <StyledEngineProvider injectFirst>
                                                    <ThemeProvider theme={LightTheme}>
                                                        <Order />
                                                    </ThemeProvider>
                                                </StyledEngineProvider>
                                            )
                                        }}
                                    </Route>
                                    <Route path={Routes.cookies.path}>
                                        {(props) => {
                                            return (
                                                <Container maxWidth="lg" component="main"><Box pb={6}> <div id="ot-sdk-cookie-policy"></div></Box> </Container>)
                                        }}
                                    </Route>
                                </Switch>
                            </Suspense>
                        </Box>
                        {!location.pathname.startsWith(Routes.workspace.path) &&
                            <Footer />
                        }
                    </Box>
                    <Snackbar
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        open={snackbar.open}
                        onClose={handleClose}
                        key="mainSnackbar"
                        autoHideDuration={6000}
                    >
                        <Alert severity={snackbar.severity} variant="filled" >{snackbar.message}</Alert>
                    </Snackbar>
                </ThemeProvider>
            </StyledEngineProvider>
        </Box>
    );
}

export default App;